import React, { useEffect, useRef, useState } from "react";

export type EstadoPaso = "hecho" | "actual" | "pendiente" | "error";

export type PasoPedido = {
  key: string;
  icon: React.ReactNode;
  label: React.ReactNode;
  estado: EstadoPaso;
};

type Boton = { despuesDe: string; nodo: React.ReactNode };

type Props = {
  /**
   * Identifica el flujo (la entrega o el pago) para recordar hasta dónde se había visto y
   * animar el avance aunque el componente se vuelva a montar al refrescar el pedido.
   */
  id?: string;
  /** Lo que abre el flujo, antes del primer paso: la factura en una entrega. */
  inicio?: React.ReactNode;
  pasos: PasoPedido[];
  /**
   * El botón que cambia de estado va **sobre la línea entre el paso actual y el siguiente**
   * (`despuesDe` es la key del actual), no debajo del nombre: así ningún paso es más alto
   * que los demás y la línea queda siempre a la altura del círculo.
   */
  boton?: Boton;
  /** Lo que cierra el flujo, después del último paso: las entregas en un pago. */
  final?: React.ReactNode;
};

/** Lo que tarda el botón anterior en desvanecerse; el mismo valor está en `config.css`. */
const SALIDA_MS = 150;

/**
 * El botón de avanzar sigue cargando **hasta que llega el estado nuevo**, no hasta que
 * responde el pedido de cambio: entre una cosa y la otra el pedido se vuelve a cargar, y sin
 * esto el botón volvía a estar listo sobre el estado viejo. Si el estado no cambia —el cambio
 * falló—, se libera solo a los 8 segundos.
 */
export const useEsperaDelAvance = (paso: number) => {
  const [esperando, setEsperando] = useState(false);
  const timer = useRef<number>();

  useEffect(() => {
    setEsperando(false);
    window.clearTimeout(timer.current);
  }, [paso]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const esperar = () => {
    setEsperando(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setEsperando(false), 8000);
  };

  return { esperando, esperar, liberar: () => setEsperando(false) };
};

/** Hasta qué paso llegó cada flujo la última vez que se dibujó. */
const visto = new Map<string, number>();

const conLinea = (estado?: EstadoPaso) =>
  estado === "hecho" || estado === "actual";

/**
 * Flujo de estado de una entrega o de un pago. Armado a mano y no con los Steps de antd por
 * lo mismo que el del producto en Catálogo: antd dibuja la línea con un `::after` que no deja
 * meter el botón en el medio, y la ubica a la mitad de la altura de cada paso, que cambia si
 * el paso tiene algo abajo. Usa las mismas clases `flujo-estado` de `config.css`.
 *
 * **Un cambio de estado se anima todo a la vez:** el botón anterior se desvanece, la línea
 * se llena de verde, se prende el círculo, el espacio del botón se corre al tramo siguiente y
 * aparece el botón nuevo, en un solo movimiento.
 */
export const FlujoPedido: React.FC<Props> = ({
  id,
  inicio,
  pasos,
  boton,
  final,
}) => {
  const hayError = pasos.some((p) => p.estado === "error");
  // El paso más avanzado al que se llegó: hecho o actual.
  const nivel = pasos.reduce((n, p, i) => (conLinea(p.estado) ? i : n), -1);

  // Si el flujo se volvió a montar después de un avance, se dibuja primero como estaba y en
  // el cuadro siguiente como está, para que las transiciones tengan desde dónde partir.
  const [mostrado, setMostrado] = useState(() => {
    const antes = id ? visto.get(id) : undefined;
    return antes !== undefined && !hayError ? Math.min(antes, nivel) : nivel;
  });
  const [saliente, setSaliente] = useState<Boton>();

  const botonPrevio = useRef<Boton | undefined>(boton);
  const nivelPrevio = useRef(nivel);

  useEffect(() => {
    if (id) visto.set(id, nivel);
    const avanzo = nivel > nivelPrevio.current;
    nivelPrevio.current = nivel;

    let cuadro = 0;
    if (mostrado !== nivel) {
      cuadro = requestAnimationFrame(() => setMostrado(nivel));
    }

    const anterior = botonPrevio.current;
    if (!avanzo || !anterior || anterior.despuesDe === boton?.despuesDe) {
      return () => cancelAnimationFrame(cuadro);
    }
    setSaliente(anterior);
    const timer = window.setTimeout(() => setSaliente(undefined), SALIDA_MS);
    return () => {
      cancelAnimationFrame(cuadro);
      window.clearTimeout(timer);
    };
    // Sólo un cambio de paso dispara la animación.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, nivel]);

  // Después del efecto de arriba, que necesita el botón que había antes del cambio.
  useEffect(() => {
    botonPrevio.current = boton;
  });

  const estadoVisible = (paso: PasoPedido, i: number): EstadoPaso =>
    hayError || i <= mostrado ? paso.estado : "pendiente";

  const conector = (
    hecho: boolean,
    error: boolean,
    despuesDe?: string,
  ) => {
    const nuevo = despuesDe !== undefined && boton?.despuesDe === despuesDe;
    const sale = despuesDe !== undefined && saliente?.despuesDe === despuesDe;
    return (
      <div
        className={[
          "flujo-estado__conector",
          hecho && "flujo-estado__conector--hecho",
          error && "flujo-estado__conector--error",
          nuevo && "flujo-estado__conector--con-boton",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="flujo-estado__linea" />
        {nuevo && (
          <>
            <div className="flujo-pedido__boton flujo-pedido__boton--entra">
              {boton.nodo}
            </div>
            <div className="flujo-estado__linea" />
          </>
        )}
        {sale && (
          <div className="flujo-pedido__boton flujo-pedido__boton--sale">
            {saliente.nodo}
          </div>
        )}
      </div>
    );
  };

  const ultimo = pasos[pasos.length - 1];
  const ultimoVisible = ultimo && estadoVisible(ultimo, pasos.length - 1);

  return (
    <div className="flujo-pedido">
      <div className="flujo-estado">
        {inicio && (
          <>
            <div className="flujo-pedido__extremo">{inicio}</div>
            {conector(
              conLinea(pasos[0] && estadoVisible(pasos[0], 0)),
              pasos[0]?.estado === "error",
            )}
          </>
        )}

        {pasos.map((paso, i) => {
          const siguiente = pasos[i + 1];
          return (
            <React.Fragment key={paso.key}>
              <div
                className={`flujo-estado__paso flujo-estado__paso--${estadoVisible(paso, i)}`}
              >
                <div className="flujo-estado__circulo">{paso.icon}</div>
                <div className="flujo-estado__nombre">{paso.label}</div>
              </div>
              {siguiente &&
                conector(
                  conLinea(estadoVisible(siguiente, i + 1)),
                  siguiente.estado === "error",
                  paso.key,
                )}
            </React.Fragment>
          );
        })}

        {final && (
          <>
            {conector(conLinea(ultimoVisible), ultimo?.estado === "error")}
            <div className="flujo-pedido__final">{final}</div>
          </>
        )}
      </div>
    </div>
  );
};
